<template>
  <div>
    <!-- Загрузка -->
    <div v-if="contentLoading" class="text-center py-8">
      <p class="text-gray-500">Загрузка лабораторной работы...</p>
    </div>

    <!-- Редактирование LabWork -->
    <div v-else-if="labWork">
      <!-- Название LabWork -->
      <div style="font-size: 18px" class="font-semibold inl-100 mb-3 flex-center fl-start gap-2 margin-t-15">
        <div class="mr-3 ml-1">Лаб-работа</div>
        <input
          v-model="labWork.title"
          placeholder="Введите название лабораторной работы"
          type="text"
          class="h-10 border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150 flex-1"
        />
      </div>

      <!-- Изображение LabWork -->
      <div class="ml-8 mb-3 margin-t-15">
        <ImageUploader
          ref="labWorkImageUploader"
          label="📷 Загрузить изображение лабораторной работы"
          :initialImage="labWork.image"
          @image-uploaded="(img) => labWork.image = img"
          @image-removed="() => labWork.image = null"
        />
      </div>

      <!-- Описание LabWork -->
      <div class="ml-8 mb-3">
        <label class="text-xs text-gray-600 font-semibold block mb-1">
          📝 Описание лабораторной работы:
        </label>
        <textarea
          v-model="labWork.content"
          placeholder="Описание и инструкции для лабораторной работы..."
          class="w-full h-24 border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150"
        />
      </div>

      <!-- ВОПРОСЫ LabWork -->
      <div class="ml-8 mb-5">
        <h3 class="text-lg font-semibold text-gray-700 mb-4 margin-t-15">Вопросы лабораторной работы:</h3>

        <ul
          style="width: 100%"
          class="margin-t-15 space-y-1 text-gray-500 list-inside dark:text-gray-400 text-left mb-5"
        >
          <!-- Существующие вопросы -->
          <li
            v-for="(question, qInd) in labWork.questions"
            style="width: 100%"
            class="margin-bt-15 border-l-4 border-green-500 pl-4"
            :key="qInd"
          >
            <!-- Вопрос и баллы -->
            <div style="font-size: 16px" class="font-semibold inl-100 mb-3 flex-center fl-start gap-2">
              <div class="mr-3">{{ qInd + 1 }}.</div>
              <input
                v-model="labWork.questions[qInd].question"
                placeholder="Введите вопрос"
                type="text"
                class="h-10 border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150 flex-1"
              />
              <span class="flex items-center gap-1 flex-shrink-0">
                <label class="text-xs text-gray-600 whitespace-nowrap">Баллы:</label>
                <input
                  v-model.number="labWork.questions[qInd].points"
                  type="number"
                  min="0"
                  max="100"
                  class="h-10 border-0 px-2 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150 w-16"
                />
              </span>
            </div>

            <!-- Изображение вопроса -->
            <div class="ml-8 mb-3">
              <ImageUploader
                :ref="`questionImage${qInd}`"
                label="📷 Загрузить изображение вопроса"
                :initialImage="labWork.questions[qInd].image"
                @image-uploaded="(img) => labWork.questions[qInd].image = img"
                @image-removed="() => labWork.questions[qInd].image = null"
              />
            </div>

            <!-- Объяснение вопроса -->
            <div class="ml-8 mb-3">
              <label class="text-xs text-gray-600 font-semibold block mb-1">
                💡 Объяснение правильного ответа (показывается при ошибке):
              </label>
              <textarea
                v-model="labWork.questions[qInd].explanation"
                placeholder="Объясните, почему этот ответ правильный..."
                class="w-full h-20 border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150"
              />
            </div>

            <!-- ОТВЕТЫ на вопрос -->
            <div class="ml-8">
              <div class="font-semibold text-sm text-gray-700 mb-2">Варианты ответов:</div>
              <div
                v-for="(answer, answerInd) in labWork.questions[qInd].question_answers"
                class="margin-bt-10 border border-gray-200 rounded p-3 mb-3"
                :key="answerInd"
              >
                <!-- Чекбокс правильного ответа -->
                <div class="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    :id="`answer${qInd}-${answerInd}`"
                    @change="changeCorrectAnswer(qInd, answerInd)"
                    :checked="labWork.questions[qInd].question_answers[answerInd].is_correct"
                  />
                  <label :for="`answer${qInd}-${answerInd}`" class="text-xs text-gray-600">
                    ✓ Правильный ответ
                  </label>
                </div>

                <!-- Текст ответа -->
                <input
                  v-model="labWork.questions[qInd].question_answers[answerInd].answer"
                  placeholder="Введите текст ответа"
                  type="text"
                  class="w-full h-10 border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150 mb-2"
                />

                <!-- Изображение ответа -->
                <div class="mb-2">
                  <ImageUploader
                    :ref="`answerImage${qInd}-${answerInd}`"
                    label="📷 Загрузить изображение ответа"
                    :initialImage="labWork.questions[qInd].question_answers[answerInd].image"
                    @image-uploaded="(img) => labWork.questions[qInd].question_answers[answerInd].image = img"
                    @image-removed="() => labWork.questions[qInd].question_answers[answerInd].image = null"
                  />
                </div>

                <!-- Пояснение к ответу -->
                <div class="mb-2">
                  <label class="text-xs text-gray-600 font-semibold block mb-1">
                    💡 Пояснение к этому ответу (опционально):
                  </label>
                  <textarea
                    v-model="labWork.questions[qInd].question_answers[answerInd].explanation"
                    placeholder="Пояснение того, почему этот ответ правильный/неправильный..."
                    class="w-full h-16 border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150"
                  />
                </div>

                <!-- Кнопка удаления ответа -->
                <button
                  type="button"
                  @click="deleteAnswer(qInd, answerInd)"
                  class="w-full bg-red-500 text-white active:bg-red-600 font-bold uppercase text-xs px-3 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
                >
                  ✕ Удалить ответ
                </button>
              </div>

              <!-- Добавить новый ответ -->
              <input
                v-on:keyup.enter="addAnswer($event, qInd)"
                placeholder="+ Введите новый ответ и нажмите Enter"
                type="text"
                class="w-full h-10 border-2 border-dashed border-green-400 px-3 py-3 placeholder-green-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring ease-linear transition-all duration-150"
              />
            </div>

            <!-- Кнопка удаления вопроса -->
            <button
              type="button"
              @click="deleteQuestion(qInd)"
              class="ml-8 mt-3 bg-red-500 text-white active:bg-red-600 font-bold uppercase text-xs px-3 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
              style="width: calc(100% - 32px)"
            >
              ✕ Удалить вопрос
            </button>
          </li>

          <!-- Добавить новый вопрос -->
          <li style="width: 100%">
            <div style="font-size: 18px" class="margin-t-30 font-semibold inl-100 mb-3">
              <input
                v-on:keyup.enter="onQuestionEnter"
                placeholder="Создайте новый вопрос"
                type="text"
                class="h-10 border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
              />
            </div>
          </li>
        </ul>
      </div>

      <!-- Кнопка сохранения -->
      <button
        type="button"
        @click="saveData"
        :disabled="isSaving"
        class="bg-lightBlue-500 text-white active:bg-lightBlue-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-500"
        style="width: 30%"
      >
        <span v-if="!isSaving">Сохранить</span>
        <span v-else class="inline-flex items-center gap-2">
          <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Сохранение...
        </span>
      </button>
    </div>

    <!-- Если нет LabWork -->
    <div v-else class="text-center py-8">
      <p class="text-gray-500">Лабораторная работа не найдена</p>
    </div>
  </div>
</template>

<script>
import { API_CLIENT, HEADERS_TOKEN_JSON } from "@/const/uri";
import axios from "axios";
import { mapActions } from 'vuex';
import ImageUploader from './ImageUploader.vue';

export default {
  emits: ['active'],
  components: {
    ImageUploader,
  },
  data() {
    return {
      contentLoading: true,
      labWork: null,
      isSaving: false,
    };
  },
  mounted() {
    this.loadData();
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  },
  beforeRouteUpdate(to, from, next) {
    this.loadData();
    next();
  },
  watch: {
    labWork(val) {
      const active = val !== null;
      this.setBeforeUnload(active);
      this.$emit('active', active);
    },
  },
  methods: {
    ...mapActions(["toggleAlert"]),

    onBeforeUnload(e) {
      e.preventDefault();
      e.returnValue = '';
    },
    setBeforeUnload(active) {
      if (active) {
        window.addEventListener('beforeunload', this.onBeforeUnload);
      } else {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
      }
    },

    changeCorrectAnswer(qInd, answerInd) {
      this.labWork.questions[qInd].question_answers[answerInd].is_correct =
        !this.labWork.questions[qInd].question_answers[answerInd].is_correct;
    },

    addAnswer(event, qInd) {
      const value = event.target.value;
      this.labWork.questions[qInd].question_answers.push({
        answer: value,
        is_correct: false,
        image: null,
        explanation: '',
      });
      event.target.value = '';
    },

    deleteAnswer(qInd, answerInd) {
      const answer = this.labWork.questions[qInd].question_answers[answerInd];
      console.log("Deleting answer:", answer);
      if (answer.id) {
        this.deleteAnswerFromAPI(answer.id, qInd, answerInd);
      } else {
        this.labWork.questions[qInd].question_answers.splice(answerInd, 1);
      }
    },

    async deleteAnswerFromAPI(answerId, qInd, answerInd) {
      try {
        await axios({
          method: "DELETE",
          url: `${API_CLIENT.adminLabAnswers}${answerId}/`,
          headers: HEADERS_TOKEN_JSON(),
        });
        this.labWork.questions[qInd].question_answers.splice(answerInd, 1);
        this.toggleAlert({
          message: "Ответ успешно удален",
          isError: false,
        });
      } catch (error) {
        console.error("Ошибка при удалении ответа:", error);
        this.toggleAlert({
          message: "Ошибка при удалении ответа",
          isError: true,
        });
      }
    },

    deleteQuestion(qInd) {
      const question = this.labWork.questions[qInd];
      if (question.id) {
        this.deleteQuestionFromAPI(question.id, qInd);
      } else {
        this.labWork.questions.splice(qInd, 1);
      }
    },

    async deleteQuestionFromAPI(questionId, qInd) {
      try {
        await axios({
          method: "DELETE",
          url: `${API_CLIENT.adminLabQuestionsItem}${questionId}/`,
          headers: HEADERS_TOKEN_JSON(),
        });
        this.labWork.questions.splice(qInd, 1);
        this.toggleAlert({
          message: "Вопрос успешно удален",
          isError: false,
        });
      } catch (error) {
        console.error("Ошибка при удалении вопроса:", error);
        this.toggleAlert({
          message: "Ошибка при удалении вопроса",
          isError: true,
        });
      }
    },

    onQuestionEnter(e) {
      const val = e.target.value;
      if (val == '') return;
      this.labWork.questions.push({
        question: val,
        question_answers: [],
        points: 1,
        image: null,
        explanation: '',
      });
      e.target.value = '';
    },

    validateQuestions() {
      for (let qInd = 0; qInd < this.labWork.questions.length; qInd++) {
        const question = this.labWork.questions[qInd];
        if (!question.question.trim()) {
          this.toggleAlert({
            message: `Вопрос ${qInd + 1}: Текст вопроса не может быть пустым`,
            isError: true,
          });
          return false;
        }

        const hasCorrectAnswer = question.question_answers.some(answer => answer.is_correct);
        if (!hasCorrectAnswer) {
          this.toggleAlert({
            message: `Вопрос "${question.question}": Должен быть отмечен хотя бы один правильный ответ`,
            isError: true,
          });
          return false;
        }

        const hasAnswers = question.question_answers.length > 0;
        if (!hasAnswers) {
          this.toggleAlert({
            message: `Вопрос "${question.question}": Должен быть хотя бы один ответ`,
            isError: true,
          });
          return false;
        }
      }
      return true;
    },

    async saveData() {
      if (this.isSaving) return;

      if (!this.validateQuestions()) {
        return;
      }

      this.isSaving = true;
      try {
        // Отправляем LabWork вместе с вопросами и ответами
        const labWorkData = {
          title: this.labWork.title,
          content: this.labWork.content,
          image: this.labWork.image,
          index: this.labWork.index || 0,
          questions: this.labWork.questions,
        };

        await axios({
          method: "PUT",
          url: API_CLIENT.labWorks + this.$route.params.themeUUID + "/",
          headers: HEADERS_TOKEN_JSON(),
          data: {
            lab_work: labWorkData,
          },
        })
          .then(() => {
            this.toggleAlert({
              message: "Лабораторная работа успешно обновлена",
              isError: false,
            });
            // Перезагружаем для обновления
            this.loadData();
          })
          .catch(function (e) {
            console.log(e);
          });
      } catch (error) {
        console.error("Ошибка при сохранении:", error);
        this.toggleAlert({
          message: "Ошибка при сохранении лабораторной работы",
          isError: true,
        });
      } finally {
        this.isSaving = false;
      }
    },

    async loadData() {
      try {
        this.contentLoading = true;
        const response = await axios({
          method: "GET",
          url: API_CLIENT.labWorks + this.$route.params.themeUUID + "/",
          headers: HEADERS_TOKEN_JSON(),
        });
        this.labWork = response.data.data.lab_work || null;

        // Если LabWork не существует, создаём пустую структуру
        if (!this.labWork) {
          this.labWork = {
            title: '',
            content: '',
            image: null,
            questions: [],
          };
        }

        this.contentLoading = false;
      } catch (error) {
        console.error("Ошибка при загрузке:", error);
        this.contentLoading = false;
        this.toggleAlert({
          message: "Ошибка при загрузке лабораторной работы",
          isError: true,
        });
      }
    },
  },
  props: {
    canEdit: {},
  },
};
</script>
